/**
 * useBackgroundNoiseDetection
 *
 * Monitors microphone input using the Web Audio API (AnalyserNode) and returns
 * `isHighNoise: true` when sustained ambient volume exceeds a threshold.
 *
 * Design:
 * - Opens its own short-lived getUserMedia stream solely for analysis; it does
 *   NOT interfere with WaveSurfer's or LiveKit's mic streams.
 * - Samples RMS (root-mean-square) volume every 500 ms.
 * - Only triggers the warning after SUSTAINED_SAMPLES consecutive high readings
 *   (default 6 = 3 seconds) to avoid false positives from single loud events.
 * - Resets the sustained count if a low reading is detected, preventing a
 *   permanently-stuck warning after noise clears.
 * - Automatically tears down AudioContext on unmount or when `active` goes false.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { createComponentLogger } from '@/lib/utils/logger';

const logger = createComponentLogger('useBackgroundNoiseDetection');

/** RMS threshold: 0–1 scale. 0.08 ≈ moderately loud ambient noise. */
const NOISE_THRESHOLD = 0.08;

/** Number of consecutive high-RMS samples before raising the flag (each sample = ~500 ms). */
const SUSTAINED_SAMPLES = 6; // ~3 seconds

/** How many consecutive low-RMS samples needed to clear the flag. */
const CLEAR_SAMPLES = 4; // ~2 seconds

/** Sampling interval in milliseconds. */
const SAMPLE_INTERVAL_MS = 500;

export interface UseBackgroundNoiseDetectionReturn {
  /** True when sustained high ambient noise is detected */
  isHighNoise: boolean;
}

/**
 * Compute RMS from a Float32Array of PCM samples.
 */
function computeRms(buffer: Float32Array): number {
  let sumOfSquares = 0;
  for (let i = 0; i < buffer.length; i++) {
    sumOfSquares += buffer[i] * buffer[i];
  }
  return Math.sqrt(sumOfSquares / buffer.length);
}

/**
 * @param active - Pass `true` while actively recording. Detection stops when false.
 */
export function useBackgroundNoiseDetection(active: boolean): UseBackgroundNoiseDetectionReturn {
  const [isHighNoise, setIsHighNoise] = useState(false);

  // Refs to audio resources so they survive across renders without causing
  // re-renders themselves.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const highCountRef = useRef(0);
  const lowCountRef = useRef(0);

  const teardown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    highCountRef.current = 0;
    lowCountRef.current = 0;
    setIsHighNoise(false);
  }, []);

  useEffect(() => {
    if (!active) {
      teardown();
      return;
    }

    let cancelled = false;

    async function setup() {
      let stream: MediaStream | null = null;

      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          logger.warn('Media devices are not supported in this environment');
          return;
        }

        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: false, // We WANT the raw level to detect noise
            autoGainControl: false,
            channelCount: 1,
          },
          video: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        const AudioContextClass =
          window.AudioContext ||
          (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

        if (!AudioContextClass) {
          logger.warn('AudioContext is not supported in this environment');
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;

        const ctx = new AudioContextClass();
        audioCtxRef.current = ctx;

        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;

        const buffer = new Float32Array(analyser.fftSize);

        intervalRef.current = setInterval(() => {
          if (!analyserRef.current) return;
          analyserRef.current.getFloatTimeDomainData(buffer);
          const rms = computeRms(buffer);

          if (rms > NOISE_THRESHOLD) {
            highCountRef.current += 1;
            lowCountRef.current = 0;
            if (highCountRef.current >= SUSTAINED_SAMPLES) {
              setIsHighNoise(true);
            }
          } else {
            lowCountRef.current += 1;
            highCountRef.current = 0;
            if (lowCountRef.current >= CLEAR_SAMPLES) {
              setIsHighNoise(false);
            }
          }
        }, SAMPLE_INTERVAL_MS);

        logger.log('Background noise detection started');
      } catch (err) {
        logger.warn('Could not start background noise detection:', err);
        if (stream) {
          stream.getTracks().forEach((t) => t.stop());
        }
        teardown();
      }
    }

    setup();

    return () => {
      cancelled = true;
      teardown();
    };
  }, [active, teardown]);

  return { isHighNoise };
}
